import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bookmark, ExternalLink, Sparkles, ArrowRight, Folder, Loader2, Share2, Check, Plus, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { UpgradeModal } from '../components/UpgradeModal';
import VerifiedBadge from '../components/VerifiedBadge';

const API = '/api';

/**
 * Public category page - `/c/:categoryId` - renders when a user shares a
 * collection publicly. Includes a "Save to my BrainSync" CTA for viral growth.
 */
export default function PublicCategoryPage() {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const { user, login } = useAuth();
    // Lazy-restore cached data from sessionStorage when the visitor
    // navigates BACK from a bookmark (e.g. /c/X → /share/Y → Back).
    // Without this, the page re-fetches and re-flashes the loading
    // skeleton every single back-tap, which feels broken on a curated
    // collection where users browse multiple items.
    const cacheKey = `pub_cat_${categoryId}`;
    const scrollKey = `pub_cat_scroll_${categoryId}`;
    const [data, setData] = useState(() => {
        // Preferred source: the inline SEO injector in `public/index.html`
        // stashes the resolved API payload at `window.__BS_PRELOADED__`
        // before React mounts. Using it as initial state skips the
        // duplicate fetch+loading-flash and survives the case where the
        // SSR-render reaches Google but React's refetch would have been
        // throttled / WAF-blocked, which previously produced a "Soft 404"
        // verdict.
        try {
            const preloaded = window.__BS_PRELOADED__ && window.__BS_PRELOADED__['collection:' + categoryId];
            if (preloaded && preloaded.category) return preloaded;
        } catch (_e) { /* ignore */ }
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return JSON.parse(cached);
        } catch (_e) { /* corrupt cache → ignore */ }
        return null;
    });
    const [loading, setLoading] = useState(() => {
        try {
            if (window.__BS_PRELOADED__ && window.__BS_PRELOADED__['collection:' + categoryId]) return false;
        } catch (_e) { /* ignore */ }
        try { return !sessionStorage.getItem(cacheKey); } catch { return true; }
    });
    const [error, setError] = useState(null);
    // Reshare state - must be declared above any early returns so the
    // hook order stays constant across renders (rules-of-hooks).
    const [resharing, setResharing] = useState(false);
    const [savingCollection, setSavingCollection] = useState(false);
    const [savingBookmarkId, setSavingBookmarkId] = useState(null);
    // Upsell modal - opened on a 402 from either clone endpoint when
    // the recipient is out of Premium AI quota.
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [upgradeContext, setUpgradeContext] = useState({});

    // Rating state - must also live above the early returns. Mirrors
    // the server `ratings` block so we can render the active state
    // optimistically. `myRating` is one of: 'up' | 'down' | null.
    const [myRating, setMyRating] = useState(null);
    const [, setUpCount] = useState(0);
    const [, setDownCount] = useState(0);
    const [ratingBusy, setRatingBusy] = useState(false);
    // Whenever the page payload arrives or refreshes, sync the local
    // rating state to whatever the server says.
    useEffect(() => {
        const r = data?.ratings;
        if (!r) return;
        setMyRating(r.my_rating ?? null);
        setUpCount(r.up_count || 0);
        setDownCount(r.down_count || 0);
    }, [data?.ratings]);

    // Recipient's current Premium AI quota. Fetched lazily after the
    // user authenticates so the Save CTA can show "Uses N of your X
    // remaining AI slots" *before* the user taps - pre-empting the
    // 402 with a transparent counter that converts better than a modal.
    const [quota, setQuota] = useState(null);

    useEffect(() => {
        if (!user) {
            setQuota(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/subscriptions/status`, { credentials: 'include' });
                if (!res.ok) return;
                const json = await res.json();
                if (cancelled) return;
                setQuota({
                    premium_used: Number(json.premium_used || 0),
                    premium_limit: Number(json.premium_limit ?? 0),
                    tier: json.tier || 'free',
                });
            } catch (_e) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const openUpsellFromDetail = useCallback((detail) => {
        const d = detail && typeof detail === 'object' ? detail : {};
        setUpgradeContext({
            premiumUsed: typeof d.premium_used === 'number' ? d.premium_used : undefined,
            premiumLimit: typeof d.premium_limit === 'number' ? d.premium_limit : undefined,
            tier: d.tier || 'free',
        });
        setUpgradeOpen(true);
        if (d.message) toast.error(d.message);
    }, []);

    const handleSaveCollection = async () => {
        if (savingCollection) return;
        if (!user) {
            // Unauth → land them on the standard homepage hero (Google
            // CTA prominent, email as secondary). The ?next= param is
            // consumed by LandingPage post-auth so they bounce back to
            // this collection automatically once signed in.
            navigate(`/?next=${encodeURIComponent(`/c/${categoryId}?action=add`)}`);
            return;
        }
        setSavingCollection(true);
        try {
            const res = await fetch(`${API}/public/categories/${categoryId}/clone`, {
                method: 'POST',
                credentials: 'include',
            });
            if (res.status === 401) {
                toast.error('Please sign in again to save this collection.');
                return;
            }
            if (res.status === 402) {
                const body = await res.json().catch(() => ({}));
                openUpsellFromDetail(body && body.detail);
                return;
            }
            if (res.status === 403) {
                const body = await res.json().catch(() => ({}));
                toast.error((body && body.detail) || 'Not allowed to save this collection.');
                return;
            }
            if (!res.ok) throw new Error('Could not save collection.');
            const json = await res.json();
            if (json.already_owned) {
                toast.info("This is your own collection.");
                return;
            }
            const added = json.added || 0;
            const skipped = json.skipped || 0;
            if (added === 0 && skipped > 0) {
                toast.success(`Already in your library (${skipped} item${skipped === 1 ? '' : 's'}).`);
            } else {
                toast.success(`Saved ${added} new item${added === 1 ? '' : 's'}${skipped ? ` (${skipped} already saved)` : ''} to your BrainSync.`);
            }
            // Hop the user into their newly-cloned collection so they
            // can verify the save and start editing right away.
            setTimeout(() => navigate(`/dashboard?category=${json.category_id}`), 700);
        } catch (e) {
            toast.error(e.message || 'Save failed. Try again in a moment.');
        } finally {
            setSavingCollection(false);
        }
    };

    const handleSaveBookmark = async (bookmarkId) => {
        if (savingBookmarkId) return;
        if (!user) {
            navigate(`/?next=${encodeURIComponent(`/c/${categoryId}?action=add-bookmark&bookmark=${bookmarkId}`)}`);
            return;
        }
        setSavingBookmarkId(bookmarkId);
        try {
            const res = await fetch(`${API}/share/${bookmarkId}/clone`, {
                method: 'POST',
                credentials: 'include',
            });
            if (res.status === 402) {
                const body = await res.json().catch(() => ({}));
                openUpsellFromDetail(body && body.detail);
                return;
            }
            if (res.status === 403) {
                const body = await res.json().catch(() => ({}));
                toast.error((body && body.detail) || 'Not allowed to save this bookmark.');
                return;
            }
            if (!res.ok) throw new Error('Could not save bookmark.');
            const json = await res.json();
            if (json.already_owned) {
                toast.info("This is your own bookmark.");
            } else if (json.already_cloned) {
                toast.info("Already in your BrainSync.");
            } else {
                toast.success('Saved to your BrainSync.');
            }
        } catch (e) {
            toast.error(e.message || 'Save failed. Try again in a moment.');
        } finally {
            setSavingBookmarkId(null);
        }
    };

    useEffect(() => {
        // Capture referral tag from URL so we can attribute signups
        try {
            const params = new URLSearchParams(window.location.search);
            const ref = params.get('ref');
            if (ref && /^user_[a-z0-9]+$/i.test(ref)) {
                localStorage.setItem('brainsync_ref', ref);
            }
        } catch (_) { /* non-browser env */ }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Dismiss the pre-React splash immediately - the network
            // fetch below has its own skeleton; users shouldn't sit
            // behind the brand splash while the API resolves.
            window.__brainsyncDismissSplash?.();
            // If we restored cached data from sessionStorage, skip the
            // loading flash and refresh quietly in the background so
            // bookmark counts / new additions still surface.
            const hadCache = !loading && !!data;
            if (!hadCache) {
                setLoading(true);
                setError(null);
            }

            // Retry on transient failures (5xx, network errors, edge-WAF
            // challenges). Without this, a single Cloudflare bot-check
            // 503/403 during Google's render pass caused PublicCategoryPage
            // to render the "Collection unavailable" error state, which
            // wiped the SSR-prerendered content and triggered a Soft 404
            // verdict in Google Search Console. 404/410 short-circuit
            // immediately (legitimate not-found). Any other non-OK gets
            // retried up to 2 more times with backoff before surfacing
            // the error.
            const MAX_ATTEMPTS = 3;
            const BACKOFF_MS = [0, 800, 2000];
            let lastErrorMsg = null;
            let resolved = false;

            for (let attempt = 0; attempt < MAX_ATTEMPTS && !cancelled && !resolved; attempt++) {
                if (BACKOFF_MS[attempt]) {
                    await new Promise((r) => setTimeout(r, BACKOFF_MS[attempt]));
                    if (cancelled) break;
                }
                try {
                    const res = await fetch(`${API}/public/categories/${categoryId}`, { credentials: 'include' });
                    if (res.status === 404 || res.status === 410) {
                        lastErrorMsg = 'Collection not found or private.';
                        break; // hard not-found: don't retry
                    }
                    if (!res.ok) {
                        lastErrorMsg = 'Could not load collection.';
                        continue; // retry on 5xx / 403 / edge challenge
                    }
                    const json = await res.json();
                    if (!cancelled) {
                        setData(json);
                        try { sessionStorage.setItem(cacheKey, JSON.stringify(json)); } catch (_e) { /* quota */ }
                    }
                    resolved = true;
                } catch (e) {
                    lastErrorMsg = e.message || 'Could not load collection.';
                    // continue retrying on network error
                }
            }

            if (!cancelled && !resolved && !hadCache && lastErrorMsg) {
                setError(lastErrorMsg);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, cacheKey]);

    // Auto-fire the save if the visitor just signed up and came back
    // here via the `?action=add` deep-link. One-shot - we strip the
    // params after firing so a refresh doesn't double-clone.
    useEffect(() => {
        if (loading || !data || !user) return;
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'add') {
            params.delete('action');
            window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params : ''}`);
            handleSaveCollection();
        } else if (action === 'add-bookmark') {
            const bm = params.get('bookmark');
            params.delete('action');
            params.delete('bookmark');
            window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params : ''}`);
            if (bm) handleSaveBookmark(bm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data, user]);

    // Restore scroll position after the bookmark list paints - runs
    // exactly once per Back-navigation since we remove the key on use.
    useEffect(() => {
        if (loading || !data) return;
        try {
            const y = sessionStorage.getItem(scrollKey);
            if (y) {
                requestAnimationFrame(() => window.scrollTo(0, parseInt(y, 10)));
                sessionStorage.removeItem(scrollKey);
            }
        } catch (_e) { /* ignore */ }
    }, [loading, data, scrollKey]);

    // ── SEO: per-collection title, meta, canonical, and BreadcrumbList.
    // Without this, every public category indexed under the bare
    // homepage title and Google had no breadcrumb signal for the
    // curator → collection hierarchy. Cleaned up on unmount so
    // Dashboard / Landing don't inherit the schema.
    useEffect(() => {
        if (!data || !data.category) return;
        const catName = data.category.name || 'Collection';
        const curator = data.sharer_name || data.sharer_username || 'a curator';
        const url = `https://brainsync.pro/c/${categoryId}`;
        const title = `${catName} - Curated by ${curator} on BrainSync`;
        const count = (data.bookmarks || []).length;
        // Curator-supplied tagline wins over the generated fallback so
        // share previews and the document description speak in the
        // user's voice.
        const customDesc = (data.category.description || '').trim();
        const desc = customDesc
            ? customDesc
            : (count > 0
                ? `${catName}: ${count} curated link${count === 1 ? '' : 's'} hand-picked by ${curator} on BrainSync.`
                : `${catName}: a curated collection by ${curator} on BrainSync.`);

        document.title = title;

        const setMeta = (selector, attr, content) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                const m = selector.match(/\[(name|property)="([^"]+)"\]/);
                if (m) el.setAttribute(m[1], m[2]);
                document.head.appendChild(el);
            }
            el.setAttribute(attr, content);
        };
        setMeta('meta[name="description"]', 'content', desc);
        setMeta('meta[property="og:title"]', 'content', title);
        setMeta('meta[property="og:description"]', 'content', desc);
        setMeta('meta[property="og:url"]', 'content', url);
        setMeta('meta[property="og:type"]', 'content', 'website');
        setMeta('meta[name="twitter:title"]', 'content', title);
        setMeta('meta[name="twitter:description"]', 'content', desc);

        let canon = document.head.querySelector('link[rel="canonical"]');
        if (!canon) {
            canon = document.createElement('link');
            canon.setAttribute('rel', 'canonical');
            document.head.appendChild(canon);
        }
        canon.setAttribute('href', url);

        // BreadcrumbList - Home > @username > [Category]. When the
        // backend didn't return a username (legacy data), we fall back
        // to the 2-level trail Home > [Category] which is still valid.
        const crumbId = 'public-category-crumb-jsonld';
        let crumb = document.getElementById(crumbId);
        if (!crumb) {
            crumb = document.createElement('script');
            crumb.type = 'application/ld+json';
            crumb.id = crumbId;
            document.head.appendChild(crumb);
        }
        const items = [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://brainsync.pro/' },
        ];
        if (data.sharer_username) {
            items.push({
                '@type': 'ListItem',
                position: items.length + 1,
                name: `@${data.sharer_username}`,
                item: `https://brainsync.pro/${data.sharer_username}`,
            });
        }
        items.push({
            '@type': 'ListItem',
            position: items.length + 1,
            name: catName,
            item: url,
        });
        crumb.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items,
        });

        // ── CollectionPage + ItemList ─────────────────────────────────
        // Google treats CollectionPage with an embedded ItemList as a
        // list-style rich result candidate - same SERP treatment as
        // Pocket / Raindrop collection pages. Each bookmark becomes a
        // ListItem with name, url, and (where available) an image
        // thumbnail. We cap at 30 items: enough for Google's list
        // preview, small enough to keep the JSON-LD under typical size
        // limits (oversized markup gets dropped entirely).
        const listId = 'public-category-list-jsonld';
        // The inline SEO injector in index.html emits an early
        // CollectionPage + ItemList + BreadcrumbList so the prerender has
        // one even if the bundle or the API call fails. Now that we're
        // mounted with the richer data, drop all three so the URL never
        // carries duplicate ItemList / CollectionPage / BreadcrumbList.
        document.querySelectorAll('script[data-bs-entity-ld="collection"], script[data-bs-breadcrumb], script[data-bs-itemlist]')
            .forEach((el) => el.remove());
        let listEl = document.getElementById(listId);
        if (!listEl) {
            listEl = document.createElement('script');
            listEl.type = 'application/ld+json';
            listEl.id = listId;
            document.head.appendChild(listEl);
        }
        const listItems = (data.bookmarks || []).slice(0, 30).map((b, i) => {
            const item = {
                '@type': 'ListItem',
                position: i + 1,
                url: b.url,
                name: b.title || 'Untitled',
            };
            // Absolutize the thumbnail URL - Google's structured-data
            // parser is strict about images: relative paths often get
            // dropped from the rich-result preview, and social
            // unfurlers (Slack, Discord, LinkedIn) won't resolve them
            // at all.
            if (b.thumbnail) {
                item.image = b.thumbnail.startsWith('http')
                    ? b.thumbnail
                    : `https://brainsync.pro${b.thumbnail.startsWith('/') ? '' : '/'}${b.thumbnail}`;
            }
            // Surfacing the AI summary inside each ListItem doubles
            // the indexable text per page and gives Google a clear
            // per-link snippet. Cap at 320 chars so the JSON-LD blob
            // stays comfortably under Google's payload limit even
            // for 30-item collections.
            const snippet = (b.summary || '').trim();
            if (snippet) item.description = snippet.slice(0, 320);
            return item;
        });
        listEl.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': url,
            url,
            name: catName,
            description: desc,
            inLanguage: 'en',
            isPartOf: { '@id': 'https://brainsync.pro/#website' },
            author: data.sharer_username ? {
                '@type': 'Person',
                name: curator,
                identifier: data.sharer_username,
                url: `https://brainsync.pro/${data.sharer_username}`,
                image: data.sharer_picture || undefined,
            } : undefined,
            mainEntity: {
                '@type': 'ItemList',
                numberOfItems: count,
                // Honest about ordering: when the curator has manually
                // ordered the items (drag-and-drop), position 1 = top
                // pick → Ascending. Otherwise the items come back in
                // an unspecified order - telling Google "Descending"
                // for arbitrary order was wrong and could have
                // misled the SERP's "best of" interpretation.
                itemListOrder: data.category?.manual_order_enabled
                    ? 'https://schema.org/ItemListOrderAscending'
                    : 'https://schema.org/ItemListUnordered',
                itemListElement: listItems,
            },
        });

        return () => {
            document.title = 'BrainSync · Bookmarks that remember themselves';
            const stale = document.getElementById(crumbId);
            if (stale) stale.remove();
            const staleList = document.getElementById(listId);
            if (staleList) staleList.remove();
        };
    }, [data, categoryId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center" data-testid="public-category-loading">
                <Loader2 className="w-6 h-6 animate-spin text-[#10b981]" />
            </div>
        );
    }

    if (error) {
        // Previously this branch emitted `<meta name="robots" content="noindex,follow">`
        // hoping to convert a Soft 404 verdict into a milder
        // "Excluded by noindex". In practice it produced explicit
        // non-indexing in GSC, which is worse than Soft 404 (Google
        // stops trying to re-evaluate). The real safeguard against
        // crawlers seeing this state is the SSR + crawler-skip in
        // `src/index.js`. We keep this UI for real users only.
        return (
            <div className="min-h-screen bg-[#f5f5f0] flex flex-col items-center justify-center p-6" data-testid="public-category-error">
                <h1 className="text-xl font-bold text-gray-900 mb-2">Collection unavailable</h1>
                <p className="text-sm text-gray-500 mb-4 text-center max-w-sm">{error}</p>
                <Button onClick={() => navigate('/')} className="rounded-full bg-[#2E3A2F] hover:bg-[#3F4F40] text-white">Go home</Button>
            </div>
        );
    }

    const { category, sharer_name, sharer_picture, sharer_username, sharer_is_verified, bookmarks = [], ratings: serverRatings } = data || {};

    const canRate = !!serverRatings?.can_rate;

    const submitRating = async (next) => {
        if (ratingBusy) return;
        if (!user) {
            // Bounce to sign-up with a return path so the rating
            // button is immediately available after auth.
            navigate(`/?next=${encodeURIComponent(`/c/${categoryId}`)}`);
            return;
        }
        // Toggling the active vote off retracts; tapping the opposite
        // swaps; tapping fresh sets. The server treats `null` as retract.
        const desired = myRating === next ? null : next;
        const prev = myRating;
        setMyRating(desired);
        setRatingBusy(true);
        try {
            const res = await fetch(`${API}/public/categories/${categoryId}/rate`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rating: desired }),
            });
            if (res.status === 403) {
                setMyRating(prev);
                toast.error("You can't rate your own collection.");
                return;
            }
            if (!res.ok) {
                setMyRating(prev);
                toast.error('Could not save your rating. Try again.');
                return;
            }
            const json = await res.json();
            setMyRating(json.my_rating ?? null);
            setUpCount(json.up_count || 0);
            setDownCount(json.down_count || 0);
        } catch (_e) {
            setMyRating(prev);
            toast.error('Could not save your rating.');
        } finally {
            setRatingBusy(false);
        }
    };

    // Re-share: prefer native share sheet on mobile + supported
    // desktops, then the APK's JS→native bridge inside the TWA wrapper,
    // then a clipboard copy as the universal fallback. Same strategy
    // as `BookmarkDetail.handleQuickShare` so behaviour is consistent
    // across the app.
    const handleReshare = async () => {
        if (resharing) return;
        const shareUrl = window.location.origin + '/c/' + categoryId;
        const shareTitle = `${category?.name || 'Collection'} - curated by ${sharer_name || 'a BrainSync curator'}`;
        const shareText = `Check out "${category?.name}" on BrainSync`;
        const shareMessage = shareText + '\n' + shareUrl;
        setResharing(true);
        try {
            if (typeof navigator.share === 'function') {
                // Combine into the single `text` field (no separate `url`)
                // so Android share targets like WhatsApp display the body
                // text alongside the link preview.
                try {
                    await navigator.share({ title: shareTitle, text: shareMessage });
                    return;
                } catch (err) {
                    // User cancelled - stop here, don't fall through to
                    // clipboard, which would feel like the app ignored
                    // their cancel.
                    if (err?.name === 'AbortError') return;
                    // Genuine failure (e.g. permission denied) - fall
                    // through to the native bridge / clipboard.
                }
            }
            // TWA wrapper bridge: fires Android's real share chooser
            // via `LauncherActivity.java → ShareBridge`. Only defined
            // inside the BrainSync APK.
            try {
                if (window.BrainSyncNative && typeof window.BrainSyncNative.share === 'function') {
                    window.BrainSyncNative.share(shareMessage);
                    return;
                }
            } catch (_e) { /* fall through to clipboard */ }
            // Universal fallback - desktop browsers without Web Share API.
            await navigator.clipboard.writeText(shareMessage);
            toast.success('Link copied to clipboard');
        } catch (_e) {
            // Last-resort silent failure - every realistic path above
            // has already raised user feedback. Avoid a useless error
            // toast when the user simply cancelled.
        } finally {
            setTimeout(() => setResharing(false), 1200);
        }
    };

    return (
        <div className="min-h-screen bg-[#f5f5f0]" data-testid="public-category-page">
            {/* Hero */}
            <div className="bg-gradient-to-br from-[#2E3A2F] via-[#243424] to-[#1a2e1a] text-white relative overflow-hidden">
                {/* Subtle decorative grain - adds depth without
                    competing with content. Inline SVG so we don't
                    ship a separate asset. */}
                <div
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Ccircle cx='30' cy='30' r='1' fill='%23fff'/%3E%3C/g%3E%3C/svg%3E\")",
                    }}
                />

                {/* Top bar - BrainSync brand on the left, Share CTA on the right */}
                <div className="relative max-w-3xl mx-auto px-5 sm:px-6 pt-5 sm:pt-6 flex items-center justify-between">
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 transition-opacity hover:opacity-90"
                        aria-label="BrainSync home"
                        data-testid="public-category-brand"
                    >
                        <div className="w-8 h-8 rounded-lg overflow-hidden shadow-md shadow-black/20">
                            <img src="/icon-192.png" alt="" className="w-full h-full object-cover" />
                        </div>
                        <span className="font-heading font-bold tracking-tight text-base">BrainSync</span>
                    </a>

                    {/* Header actions - rating widget + Share. Rating
                        only shows for non-owners (server gates this
                        via `can_rate`; unauth viewers see it too but
                        a tap bounces them through signup). Counts are
                        intentionally hidden in the public view for
                        now - we keep them in state so we can surface
                        them later (curator dashboard, leaderboard,
                        etc.) without another schema change. */}
                    <div className="flex items-center gap-2">
                        {canRate && (
                            <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full p-0.5" data-testid="public-category-rating">
                                <button
                                    type="button"
                                    onClick={() => submitRating('up')}
                                    disabled={ratingBusy}
                                    aria-pressed={myRating === 'up'}
                                    aria-label={myRating === 'up' ? 'Remove your thumbs up' : 'Thumbs up this collection'}
                                    title={myRating === 'up' ? 'Remove thumbs up' : 'Thumbs up'}
                                    className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors disabled:opacity-60 ${
                                        myRating === 'up'
                                            ? 'bg-emerald-500 text-white shadow-md'
                                            : 'text-white/85 hover:bg-white/15'
                                    }`}
                                    data-testid="public-category-thumbs-up"
                                >
                                    <ThumbsUp className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => submitRating('down')}
                                    disabled={ratingBusy}
                                    aria-pressed={myRating === 'down'}
                                    aria-label={myRating === 'down' ? 'Remove your thumbs down' : 'Thumbs down this collection'}
                                    title={myRating === 'down' ? 'Remove thumbs down' : 'Thumbs down'}
                                    className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors disabled:opacity-60 ${
                                        myRating === 'down'
                                            ? 'bg-red-500 text-white shadow-md'
                                            : 'text-white/85 hover:bg-white/15'
                                    }`}
                                    data-testid="public-category-thumbs-down"
                                >
                                    <ThumbsDown className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Re-share CTA - viral loop entry point */}
                        <button
                            type="button"
                            onClick={handleReshare}
                            disabled={resharing}
                            className="inline-flex items-center gap-1.5 bg-white text-[#2E3A2F] hover:bg-white/95 transition-colors rounded-full px-3.5 py-2 text-sm font-semibold shadow-lg disabled:opacity-70"
                            aria-label="Share this collection"
                            data-testid="public-category-reshare-btn"
                        >
                            {resharing ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
                            <span className="hidden sm:inline">Share</span>
                        </button>
                    </div>
                </div>

                {/* Title block */}
                <div className="relative max-w-3xl mx-auto px-5 sm:px-6 pt-10 sm:pt-14 pb-10 sm:pb-14">
                    <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.15em] mb-5">
                        <Folder className="w-3 h-3" />
                        Collection
                    </div>
                    <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-5" data-testid="public-category-title">
                        {category?.name || 'Collection'}
                    </h1>
                    {category?.description && (
                        <p
                            className="text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed -mt-2 mb-4"
                            data-testid="public-category-description"
                        >
                            {category.description}
                        </p>
                    )}
                    {sharer_name && (
                        <div className="flex items-center gap-3 mt-5" data-testid="public-category-sharer">
                            {sharer_picture && (
                                sharer_username ? (
                                    <a
                                        href={`/${sharer_username}`}
                                        aria-label={`View ${sharer_name}'s profile`}
                                        data-testid="public-category-sharer-avatar-link"
                                    >
                                        <img
                                            src={sharer_picture}
                                            alt={sharer_name}
                                            className="w-9 h-9 rounded-full border border-white/20 hover:border-white/60 transition-colors"
                                        />
                                    </a>
                                ) : (
                                    <img src={sharer_picture} alt={sharer_name} className="w-9 h-9 rounded-full border border-white/20" />
                                )
                            )}
                            <div className="text-sm text-white/80">
                                Curated by{' '}
                                {sharer_username ? (
                                    <a
                                        href={`/${sharer_username}`}
                                        className="font-semibold text-white hover:underline underline-offset-4"
                                        data-testid="public-category-sharer-name-link"
                                    >
                                        {sharer_name}
                                    </a>
                                ) : (
                                    <span className="font-semibold text-white">{sharer_name}</span>
                                )}
                                {sharer_is_verified && (
                                    <VerifiedBadge size="sm" className="ml-1" />
                                )}
                                <span className="mx-2 text-white/40">·</span>
                                <span className="text-white/60">{bookmarks.length} bookmark{bookmarks.length === 1 ? '' : 's'}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Bookmarks grid */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                {/* ── Save-whole-collection CTA ──────────────────────
                    Visible above the grid so it's the first action a
                    visitor sees. For unauth visitors, it routes to the
                    sign-up flow with `?next=/c/{id}&action=add` so the
                    clone fires right after signup. Owners see a
                    disabled "Already yours" pill. */}
                {bookmarks.length > 0 && (() => {
                    // Count source bookmarks that carry AI work - these
                    // are what the backend gate counts against the
                    // recipient's Premium quota.
                    const aiCount = bookmarks.filter(b => (b.summary || '').trim()).length;
                    // Build a transparent subline: tells the user *now*
                    // whether the clone fits their plan, instead of
                    // surprising them with a 402 modal mid-tap.
                    let quotaLine = null;
                    let willOverflow = false;
                    if (user && quota && aiCount > 0) {
                        if (quota.premium_limit === -1) {
                            quotaLine = 'AI summaries included - your plan is Unlimited.';
                        } else {
                            const remaining = Math.max(0, quota.premium_limit - quota.premium_used);
                            if (aiCount > remaining) {
                                willOverflow = true;
                                quotaLine = `Uses ${aiCount} AI slot${aiCount === 1 ? '' : 's'} - only ${remaining} left on your plan. Upgrade to save the full collection.`;
                            } else {
                                quotaLine = `Uses ${aiCount} of your ${remaining} remaining AI slot${remaining === 1 ? '' : 's'}.`;
                            }
                        }
                    } else if (user && quota && aiCount === 0) {
                        quotaLine = 'All Basic items - no AI slots used.';
                    }
                    return (
                        <div className={`mb-6 rounded-2xl border p-3 sm:p-4 ${willOverflow ? 'border-amber-300 bg-amber-50/70' : 'border-emerald-200 bg-emerald-50/60'}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className={`text-sm sm:text-base font-semibold ${willOverflow ? 'text-amber-900' : 'text-emerald-900'}`}>
                                        Save this collection to your BrainSync
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (willOverflow) {
                                            // Pre-empt the 402 - open the upsell directly
                                            // so the user doesn't experience a "click,
                                            // wait, see error" flash.
                                            const remaining = quota ? Math.max(0, quota.premium_limit - quota.premium_used) : 0;
                                            openUpsellFromDetail({
                                                tier: quota?.tier || 'free',
                                                premium_used: quota?.premium_used,
                                                premium_limit: quota?.premium_limit,
                                                message: `This collection needs ${aiCount} AI slots - you have ${remaining} left.`,
                                            });
                                            return;
                                        }
                                        handleSaveCollection();
                                    }}
                                    disabled={savingCollection}
                                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-60 transition-colors ${willOverflow ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                                    data-testid="save-collection-cta"
                                >
                                    {savingCollection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    {!user ? 'Sign up to save' : (willOverflow ? 'Upgrade & save' : 'Save collection')}
                                </button>
                            </div>
                            {quotaLine && (
                                <p
                                    className={`text-[11px] sm:text-xs mt-2 ${willOverflow ? 'text-amber-800 font-medium' : 'text-emerald-700/90'}`}
                                    data-testid="save-collection-quota-preview"
                                >
                                    {quotaLine}
                                </p>
                            )}
                        </div>
                    );
                })()}
                {bookmarks.length === 0 ? (
                    <p className="text-center text-sm text-gray-500 py-10" data-testid="public-category-empty">
                        No public bookmarks in this collection yet.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="public-category-bookmarks">
                        {bookmarks.map((bm, idx) => (
                            <div key={bm.bookmark_id} className="relative">
                                {/* Rank badge - rendered OUTSIDE the
                                    overflow-hidden `<a>` so the badge
                                    doesn't get clipped at the card
                                    corners. The wrapper has `relative`
                                    so we can position the badge over
                                    the card top-left. */}
                                {category?.show_numbering && (
                                    <span
                                        className="absolute -top-2 -left-2 z-10 min-w-[32px] h-8 px-2 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-sm font-bold tabular-nums shadow-md ring-2 ring-white pointer-events-none"
                                        data-testid={`public-rank-badge-${bm.bookmark_id}`}
                                    >
                                        #{idx + 1}
                                    </span>
                                )}
                                {/* Per-bookmark Save button - pinned
                                    top-right of the card so a recipient
                                    can save individual picks without
                                    opening the share page. Outside the
                                    overflow-hidden `<a>` so the round
                                    button isn't clipped at the card
                                    corner. */}
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSaveBookmark(bm.bookmark_id); }}
                                    disabled={savingBookmarkId === bm.bookmark_id}
                                    className="absolute -top-2 -right-2 z-10 w-8 h-8 inline-flex items-center justify-center rounded-full bg-white text-emerald-700 shadow-md ring-2 ring-emerald-100 hover:bg-emerald-50 active:scale-95 disabled:opacity-60 transition-all"
                                    title={user ? 'Save to your BrainSync' : 'Sign up to save'}
                                    aria-label="Save bookmark to my BrainSync"
                                    data-testid={`public-bookmark-save-${bm.bookmark_id}`}
                                >
                                    {savingBookmarkId === bm.bookmark_id
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <Plus className="w-4 h-4" />}
                                </button>
                                <a
                                    href={`/share/${bm.bookmark_id}`}
                                    onClick={() => {
                                        // Stash the current scroll
                                        // position so a Back navigation
                                        // from /share/{id} restores the
                                        // visitor's exact spot in the
                                        // grid. Doesn't preventDefault -
                                        // the normal navigation still
                                        // runs.
                                        try { sessionStorage.setItem(scrollKey, String(window.scrollY)); } catch (_e) {}
                                        // Also leave a breadcrumb hint
                                        // so the destination /share/{id}
                                        // page can render a "← Back to
                                        // [Collection] by [Curator]" link,
                                        // closing the viral loop back to
                                        // the curator's library.
                                        try {
                                            sessionStorage.setItem('share_referrer', JSON.stringify({
                                                bookmark_id: bm.bookmark_id,
                                                category_id: categoryId,
                                                category_name: category?.name || 'Collection',
                                                curator: sharer_name || sharer_username || '',
                                            }));
                                        } catch (_e) {}
                                    }}
                                    className="group block bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
                                    data-testid={`public-category-bookmark-${bm.bookmark_id}`}
                                >
                                {bm.thumbnail && (
                                    <img
                                        src={bm.thumbnail.startsWith('http') ? bm.thumbnail : (bm.thumbnail.startsWith('/api/') ? bm.thumbnail : `/api/thumbnails/${bm.thumbnail}`)}
                                        alt={bm.title}
                                        className="w-full h-40 object-cover"
                                        loading="lazy"
                                    />
                                )}
                                <div className="p-4">
                                    <h3 className="font-heading text-sm font-semibold text-gray-900 line-clamp-2 mb-1.5 group-hover:text-emerald-700 transition-colors">
                                        {bm.title || 'Untitled'}
                                    </h3>
                                    {bm.summary && (
                                        <p className="text-xs text-gray-500 line-clamp-2">
                                            {bm.summary.split('\n').find(l => l.trim())?.slice(0, 140)}
                                        </p>
                                    )}
                                    {bm.tags && bm.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2.5">
                                            {bm.tags.slice(0, 3).map(t => (
                                                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                                                    #{t}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                        <span className="text-[11px] uppercase tracking-wider text-gray-400 font-mono">{bm.content_type || 'article'}</span>
                                        <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-emerald-600 transition-colors" />
                                    </div>
                                </div>
                            </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Viral CTA - closes the recipient → signup loop. Uses
                the same brain illustration as the Play Store listing
                and dashboard empty state so the visual identity is
                continuous: install Play Store → land here → install. */}
            {!user && (
                <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
                    <div
                        className="relative overflow-hidden rounded-3xl shadow-[0_24px_60px_-24px_rgba(30,58,138,0.4)] bg-[#1e3a8a]"
                        data-testid="public-category-cta"
                    >
                        <div className="grid sm:grid-cols-[1fr,260px] gap-0">
                            {/* Copy + CTA */}
                            <div className="relative z-10 p-7 sm:p-9 text-white">
                                <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.15em] mb-4">
                                    <Sparkles className="w-3 h-3" />
                                    Save anything yourself
                                </div>
                                <h2 className="font-heading text-2xl sm:text-[28px] font-bold tracking-tight leading-[1.15] mb-3">
                                    BrainSync turns any link into a searchable second brain.
                                </h2>
                                <p className="text-sm text-white/75 mb-6 max-w-sm">
                                    Videos transcribed, articles summarized, recipes structured. 20 free AI saves to start - unlimited basic forever.
                                </p>
                                <Button
                                    onClick={() => login()}
                                    className="rounded-full px-6 py-5 bg-white text-[#1e3a8a] hover:bg-white/95 font-semibold shadow-lg"
                                    data-testid="public-category-signup-btn"
                                >
                                    Try it free
                                    <ArrowRight className="w-4 h-4 ml-1.5" />
                                </Button>
                            </div>

                            {/* Brain illustration - desktop only so the
                                card stays compact on mobile. Uses the
                                BrainSync brand mark (icon-512) so the
                                visual identity matches the Play Store
                                listing, browser tab favicon, Android
                                home-screen icon, and dashboard splash. */}
                            <div className="hidden sm:flex relative items-center justify-center p-8">
                                <img
                                    src="/icon-512.png"
                                    alt=""
                                    aria-hidden="true"
                                    className="w-full h-auto max-h-[220px] object-contain rounded-[22%] shadow-[0_16px_48px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {user && (
                <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 text-center">
                    <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full">
                        <Bookmark className="w-4 h-4 mr-1.5" /> Back to my BrainSync
                    </Button>
                </div>
            )}

            <footer className="text-center py-6 text-xs text-gray-400 border-t border-gray-100">
                brainsync.pro · AI-powered bookmarking
            </footer>

            <UpgradeModal
                open={upgradeOpen}
                onClose={() => setUpgradeOpen(false)}
                onPurchaseComplete={() => setUpgradeOpen(false)}
                currentTier={upgradeContext.tier || 'free'}
                premiumUsed={upgradeContext.premiumUsed}
                premiumLimit={upgradeContext.premiumLimit}
                reason="premium_quota"
            />
        </div>
    );
}
